import { useMemo } from 'react';
import { ProjectId } from 'api/models';
import { StatusGroup } from 'api/tasks';
import { usePhase, usePhasesInOrder } from 'collections/phase';
import { useProjectDetail } from './use-project-detail';
import { useSprints } from './use-sprints';
export const getTaskCounter = (fields = [])=>{
    var _fields_filter, _fields_filter1, _fields_filter2, _fields_filter3;
    return {
        completed: ((_fields_filter = fields.filter((subTaskStatus)=>{
            var _subTaskStatus_metaFieldValue;
            return ((_subTaskStatus_metaFieldValue = subTaskStatus.metaFieldValue) === null || _subTaskStatus_metaFieldValue === void 0 ? void 0 : _subTaskStatus_metaFieldValue.group) === StatusGroup.Completed;
        })) === null || _fields_filter === void 0 ? void 0 : _fields_filter.length) || 0,
        inProgress: ((_fields_filter1 = fields.filter((subTaskStatus)=>{
            var _subTaskStatus_metaFieldValue;
            return ((_subTaskStatus_metaFieldValue = subTaskStatus.metaFieldValue) === null || _subTaskStatus_metaFieldValue === void 0 ? void 0 : _subTaskStatus_metaFieldValue.group) === StatusGroup.InProgress;
        })) === null || _fields_filter1 === void 0 ? void 0 : _fields_filter1.length) || 0,
        todo: ((_fields_filter2 = fields.filter((subTaskStatus)=>{
            var _subTaskStatus_metaFieldValue;
            return ((_subTaskStatus_metaFieldValue = subTaskStatus.metaFieldValue) === null || _subTaskStatus_metaFieldValue === void 0 ? void 0 : _subTaskStatus_metaFieldValue.group) === StatusGroup.Todo;
        })) === null || _fields_filter2 === void 0 ? void 0 : _fields_filter2.length) || 0,
        blocked: ((_fields_filter3 = fields.filter((subTaskStatus)=>{
            var _subTaskStatus_metaFieldValue;
            return ((_subTaskStatus_metaFieldValue = subTaskStatus.metaFieldValue) === null || _subTaskStatus_metaFieldValue === void 0 ? void 0 : _subTaskStatus_metaFieldValue.group) === StatusGroup.Blocked;
        })) === null || _fields_filter3 === void 0 ? void 0 : _fields_filter3.length) || 0
    };
};
/**
 * `phaseId` is a globally unique id (not scoped per project), so this reads
 * directly off `phaseCollection` — collection-backed (state-db-migration-
 * phase.md), no `project.projectPhases` lookup needed for a single phase.
 * `projectId` is accepted only for call-site compatibility (unused).
 */ export const useProjectPhaseFromStore = (_projectId, phaseId)=>usePhase(phaseId);
export const useProjectPhaseStore = (matchingPhaseTasks)=>{
    const firstLevelTasks = useMemo(()=>matchingPhaseTasks.filter((task)=>{
            var _task_parentTaskObject;
            return ((_task_parentTaskObject = task.parentTaskObject) === null || _task_parentTaskObject === void 0 ? void 0 : _task_parentTaskObject.taskId) == null;
        }), [
        matchingPhaseTasks
    ]);
    const firstLevelTasksFields = useMemo(()=>firstLevelTasks.reduce((result, item)=>result.concat(item.fields), []), [
        firstLevelTasks
    ]);
    const subTasksFields = useMemo(()=>matchingPhaseTasks.filter((task)=>{
            var _task_parentTaskObject;
            return ((_task_parentTaskObject = task.parentTaskObject) === null || _task_parentTaskObject === void 0 ? void 0 : _task_parentTaskObject.taskId) != null;
        }).reduce((result, item)=>result.concat(item.fields), []), [
        matchingPhaseTasks
    ]);
    const firstLevelTaskCounter = useMemo(()=>getTaskCounter(firstLevelTasksFields), [
        firstLevelTasksFields
    ]);
    const subTaskCounter = useMemo(()=>getTaskCounter(subTasksFields), [
        subTasksFields
    ]);
    return useMemo(()=>({
            tasks: firstLevelTasks,
            allTasks: matchingPhaseTasks,
            completedTaskCount: firstLevelTaskCounter.completed + subTaskCounter.completed,
            inprogressTaskCount: firstLevelTaskCounter.inProgress + subTaskCounter.inProgress,
            firstLevelTaskCounter,
            subTaskCounter
        }), [
        firstLevelTaskCounter,
        firstLevelTasks,
        matchingPhaseTasks,
        subTaskCounter
    ]);
};
/**
 * Reactive phase list for a project, collection-backed but ORDER-preserved
 * from `project.projectPhases` — that array order is the source of truth for
 * phase display order (gantt rows, phase dropdowns), NOT something
 * `phaseCollection` (keyed by phase, no ordering concept) can derive on its
 * own. `usePhasesInOrder` joins the order against live collection data.
 *
 * Falls back to the detail's own (possibly stale) phase entries if the
 * collection hasn't caught up to every id yet — should not happen in
 * practice (project-detail fetch mirrors into `phaseCollection` synchronously,
 * state-db-migration-phase.md Wave 1) but never show fewer phases than the
 * order list says exist.
 */ export const useProjectPhaseByProjectId = (projectId)=>{
    var _ref;
    const { data } = useProjectDetail(ProjectId(projectId));
    const orderedIds = useMemo(()=>{
        var _ref;
        return ((_ref = data === null || data === void 0 ? void 0 : data.projectPhases) !== null && _ref !== void 0 ? _ref : []).map((phase)=>phase.projectPhaseId);
    }, [
        data
    ]);
    const livePhases = usePhasesInOrder(orderedIds);
    return livePhases.length === orderedIds.length ? livePhases : (_ref = data === null || data === void 0 ? void 0 : data.projectPhases) !== null && _ref !== void 0 ? _ref : [];
};
export const useSprintsByProjectId = (projectId, options)=>{
    const { data } = useSprints(ProjectId(projectId), options);
    return data;
};
